import React, { useState, useEffect, useRef } from "react";
import { Link } from "gatsby";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Slide,
  Collapse,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  FaTimes,
  FaWhatsapp,
  FaBolt,
  FaShieldAlt,
  FaChevronUp,
  FaChevronDown,
  FaCheckCircle,
  FaArrowRight,
  FaLock,
} from "react-icons/fa";
import {
  getActiveIntentProfile,
  recordUserAction,
  classifyIntentByPath,
} from "../../utils/founderPsychologyEngine";
import { submitLeadToFirebase } from "../../utils/firebaseLeads";

const FounderIntentEngine = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [profile, setProfile] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [contactValue, setContactValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const scrollThrottleRef = useRef(null);

  // Initialize and track behavioral events
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Check if dismissed in this session
    const isDismissedSession = sessionStorage.getItem("goafreet_intent_dismissed") === "true";
    if (isDismissedSession) {
      setDismissed(true);
      return;
    }

    const currentPath = window.location.pathname || "/";
    const currentProfile = getActiveIntentProfile(currentPath);
    setProfile(currentProfile);

    // Record page visit
    recordUserAction("PAGE_VISIT", { path: currentPath });

    // Dwell timer: trigger non-intrusive collapsed dock after deep reading (> 25 seconds)
    const dwellTimer = setTimeout(() => {
      if (!dismissed) {
        setIsVisible(true);
      }
    }, 25000);

    // Scroll listener: trigger collapsed dock only after substantial reading (> 65%)
    const handleScroll = () => {
      if (scrollThrottleRef.current) return;

      scrollThrottleRef.current = setTimeout(() => {
        scrollThrottleRef.current = null;
        if (typeof window === "undefined" || typeof document === "undefined") return;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        const scrollPercent = scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;

        recordUserAction("SCROLL_DEPTH", { depth: scrollPercent });

        // Trigger conversion dock when visitor has deeply scrolled content (> 65%)
        if (scrollPercent >= 65 && !dismissed) {
          setIsVisible(true);
        }
      }, 300);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Track click interactions inside tools & calculators
    const handleToolInteraction = (e) => {
      const target = e.target;
      if (
        target &&
        (target.tagName === "BUTTON" ||
          target.tagName === "INPUT" ||
          target.closest("button") ||
          target.classList.contains("MuiButton-root"))
      ) {
        recordUserAction("TOOL_INTERACTION");
      }
    };

    document.addEventListener("click", handleToolInteraction);

    return () => {
      clearTimeout(dwellTimer);
      if (scrollThrottleRef.current) clearTimeout(scrollThrottleRef.current);
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("click", handleToolInteraction);
    };
  }, [dismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setDismissed(true);
    if (typeof window !== "undefined" && window.sessionStorage) {
      sessionStorage.setItem("goafreet_intent_dismissed", "true");
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!contactValue.trim()) return;

    setIsSubmitting(true);
    try {
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
      const leadPayload = {
        contact: contactValue.trim(),
        intentCategory: profile ? profile.id : "GENERAL_FOUNDER",
        intentName: profile ? profile.name : "Strategic Advisory Desk",
        painPoint: profile ? profile.painPointTag : "Direct Inquiry",
        capturedFrom: currentPath,
        action: "Scientific Conversion Dock Teardown Request",
      };

      await submitLeadToFirebase(leadPayload, { collection: "founder_leads" });
      setIsSubmitted(true);
    } catch (err) {
      setIsSubmitted(true); // Gracefully display confirmation
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!profile || !isVisible || dismissed) {
    return null;
  }

  const encodedWhatsApp = encodeURIComponent(
    `${profile.whatsappPrompt} (Found via ${typeof window !== "undefined" ? window.location.pathname : "Goafreet"})`
  );
  const whatsappUrl = `https://wa.me/9106981512?text=${encodedWhatsApp}`;

  return (
    <Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 16, sm: 24 },
          right: { xs: 12, sm: 24 },
          left: { xs: 12, sm: "auto" },
          maxWidth: { xs: "calc(100vw - 24px)", sm: isExpanded ? 460 : "auto" },
          zIndex: 1200,
        }}
      >
        <Paper
          elevation={10}
          sx={{
            borderRadius: isExpanded ? "20px" : "999px",
            overflow: "hidden",
            border: isDark ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(15,23,42,0.12)",
            background: isDark
              ? "linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)",
            backdropFilter: "blur(20px)",
            boxShadow: isDark
              ? "0 20px 45px -10px rgba(0, 0, 0, 0.7)"
              : "0 20px 40px -10px rgba(15, 23, 42, 0.16)",
            transition: "all 0.25s ease",
          }}
        >
          {/* Header Bar / Compact Pill */}
          <Box
            sx={{
              px: isExpanded ? 2.5 : 2,
              py: isExpanded ? 1.5 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              borderBottom: isExpanded ? (isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.08)") : "none",
              background: isExpanded ? (isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)") : "transparent",
              cursor: isExpanded ? "default" : "pointer",
            }}
            onClick={() => {
              if (!isExpanded) setIsExpanded(true);
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "#10b981",
                  boxShadow: "0 0 8px #10b981",
                  flexShrink: 0,
                }}
              />
              <Chip
                label={profile.badge || "Executive Strategy Desk"}
                size="small"
                sx={{
                  fontWeight: 800,
                  fontSize: "0.72rem",
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  bgcolor: isDark ? "rgba(37, 99, 235, 0.2)" : "rgba(37, 99, 235, 0.08)",
                  color: isDark ? "#60a5fa" : "#2563eb",
                  border: isDark ? "1px solid rgba(96, 165, 250, 0.3)" : "1px solid rgba(37, 99, 235, 0.22)",
                  height: 24,
                  cursor: isExpanded ? "default" : "pointer",
                }}
              />
              {!isExpanded && (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: isDark ? "#94a3b8" : "#64748b",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  Consultation Line
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                aria-label={isExpanded ? "Collapse" : "Expand"}
                sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
              >
                {isExpanded ? <FaChevronDown size={13} /> : <FaChevronUp size={13} />}
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismiss();
                }}
                aria-label="Close"
                sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
              >
                <FaTimes size={13} />
              </IconButton>
            </Box>
          </Box>

          {/* Expandable Conversion Body */}
          <Collapse in={isExpanded}>
            <Box sx={{ p: 2.5 }}>
              {isSubmitted ? (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <FaCheckCircle size={38} color="#10b981" />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      mt: 1.5,
                      color: isDark ? "#ffffff" : "#0f172a",
                      fontSize: "1.1rem",
                    }}
                  >
                    Strategy Request Received!
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: isDark ? "#94a3b8" : "#64748b", mt: 0.8, fontSize: "0.85rem" }}
                  >
                    Our senior engineering and growth team will review your project requirements and share a focused strategy teardown within 24 business hours.
                  </Typography>
                  <Button
                    component="a"
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    size="small"
                    startIcon={<FaWhatsapp />}
                    sx={{
                      mt: 2,
                      bgcolor: "#25D366",
                      "&:hover": { bgcolor: "#128C7E" },
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "10px",
                    }}
                  >
                    Fast-Track on WhatsApp
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      color: isDark ? "#ffffff" : "#0f172a",
                      lineHeight: 1.35,
                      fontSize: "1rem",
                      mb: 0.8,
                    }}
                  >
                    {profile.headline}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? "#94a3b8" : "#475569",
                      fontSize: "0.84rem",
                      lineHeight: 1.55,
                      mb: 2,
                    }}
                  >
                    {profile.subtext}
                  </Typography>

                  {/* Micro-Commitment Quick Lead Input */}
                  <Box component="form" onSubmit={handleSubmit} sx={{ mb: 1.8 }}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Your WhatsApp No. or Work Email"
                        value={contactValue}
                        onChange={(e) => setContactValue(e.target.value)}
                        variant="outlined"
                        autoComplete="tel"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            fontSize: "0.88rem",
                            bgcolor: isDark ? "rgba(0,0,0,0.25)" : "#ffffff",
                            "& fieldset": {
                              borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(15,23,42,0.15)",
                            },
                          },
                        }}
                      />
                      <Button
                        type="submit"
                        disabled={isSubmitting || !contactValue.trim()}
                        variant="contained"
                        sx={{
                          whiteSpace: "nowrap",
                          px: 2.2,
                          borderRadius: "12px",
                          fontWeight: 800,
                          textTransform: "none",
                          fontSize: "0.85rem",
                          background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                          boxShadow: "0 6px 16px -2px rgba(37, 99, 235, 0.35)",
                          "&:hover": {
                            background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                          },
                        }}
                      >
                        {isSubmitting ? <CircularProgress size={18} color="inherit" /> : <FaArrowRight />}
                      </Button>
                    </Box>
                  </Box>

                  {/* Dual Action Strip: WhatsApp & Strategy Consultation */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      pt: 1.2,
                      borderTop: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
                    }}
                  >
                    <Button
                      component="a"
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      startIcon={<FaWhatsapp color="#25D366" />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        color: isDark ? "#cbd5e1" : "#334155",
                        p: 0.5,
                      }}
                    >
                      Instant WhatsApp
                    </Button>

                    <Button
                      component={Link}
                      to="/contact-support/"
                      size="small"
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        color: isDark ? "#60a5fa" : "#2563eb",
                        p: 0.5,
                      }}
                    >
                      Book 30-Min Call →
                    </Button>
                  </Box>

                  {/* Trust & Privacy Guarantee */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.6, mt: 1.2 }}>
                    <FaLock size={10} color={isDark ? "#64748b" : "#94a3b8"} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#64748b" : "#94a3b8",
                        fontSize: "0.72rem",
                        fontWeight: 600,
                      }}
                    >
                      100% Confidential • Direct Technical Assessment • Zero Spam
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Box>
    </Slide>
  );
};

export default FounderIntentEngine;
